import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── Manual entries, accruals and reversals ──────────────────────────────────────────────────────
// Everything the derived drafts cannot produce. An entry typed here with no third-party document is
// an internal voucher (ใบสำคัญทั่วไป): the 2544 notification's ข้อ 4 wants the explanation and the
// calculation written down, so the form asks for them and the server refuses without them.
//
// Amounts are typed in baht and sent as integer satang — the ledger has no floats anywhere, and the
// conversion happens once, here, rather than in three places that drift.

type Account = { id: string; code: string; name_th: string; name_en: string; class: string };
type Posted = { id: string; entry_date: string; source_book: string; doc_no: string; narration: string; page_no: number; amount: number; reversed_by: string | null; auto_reverse_on: string };
type Line = { accountId: string; dr: string; cr: string };

// [value, statutory journal name key, gloss key]. The journal name is what the book is called in the
// statute and stays Thai in every locale; only the gloss beside it is translated.
const BOOKS: [string, string, string][] = [
  ['general', 'pa_jnl_general', 'pa_manual_book_general_gloss'], ['cash', 'pa_jnl_cash', 'pa_manual_book_cash_gloss'],
  ['bank', 'pa_jnl_bank', 'pa_manual_book_bank_gloss'],
  ['purchase', 'pa_jnl_purchase', 'pa_manual_book_purchase_gloss'], ['sale', 'pa_jnl_sale', 'pa_manual_book_sale_gloss'],
];
const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const satang = (v: string) => Math.round((parseFloat(String(v).replace(/,/g, '')) || 0) * 100);
const baht = (s: number) => (Number(s || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const btn: React.CSSProperties = {
  padding: '7px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const EMPTY: Line[] = [{ accountId: '', dr: '', cr: '' }, { accountId: '', dr: '', cr: '' }];

export default function ManualEntry({ clientId, periodId, periodClosed, onChanged }:
  { clientId: string; periodId: string; periodClosed: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [open, setOpen] = useState<'' | 'new' | 'reverse'>('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [time, setTime] = useState<any>(null);

  const [date, setDate] = useState('');
  const [book, setBook] = useState('general');
  // A bank book is kept PER ACCOUNT NUMBER, so the account is part of the entry — createManualEntry
  // refuses a bank draft without one, and journalPost refuses it again at posting. The dropdown has
  // offered the bank book since it was written while the form never sent this field, so choosing
  // บัญชีธนาคาร always failed and no bank entry has ever been made (0 in production, 2026-10-02).
  const [bankAcct, setBankAcct] = useState('');
  const [bankAccts, setBankAccts] = useState<{ id: string; entries: number; onFile: boolean }[] | null>(null);
  const [bankTyping, setBankTyping] = useState(false);
  const [docNo, setDocNo] = useState('');
  const [narration, setNarration] = useState('');
  const [calcNote, setCalcNote] = useState('');
  const [reverseOn, setReverseOn] = useState('');
  const [lines, setLines] = useState<Line[]>(EMPTY);

  const [q, setQ] = useState('');
  const [posted, setPosted] = useState<Posted[] | null>(null);

  useEffect(() => { api.jnlAccounts(clientId).then((d: any) => setAccounts(d.accounts || [])).catch(() => setAccounts([])); }, [clientId]);
  useEffect(() => {
    setBankAccts(null); setBankAcct(''); setBankTyping(false);
    api.jnlBankAccounts(clientId)
      .then((d: any) => {
        const list = d.accounts || [];
        setBankAccts(list);
        // One account is the overwhelmingly common case; preselecting it means the operator never
        // retypes the number and so can never start a second book by mistyping it.
        if (list.length === 1) setBankAcct(String(list[0].id));
        setBankTyping(list.length === 0);
      })
      .catch(() => { setBankAccts([]); setBankTyping(true); });
  }, [clientId]);

  const loadTime = useCallback(() => {
    api.jnlTimeliness(clientId, periodId).then((d: any) => setTime(d)).catch(() => setTime(null));
  }, [clientId, periodId]);
  useEffect(() => { loadTime(); }, [loadTime]);

  useEffect(() => {
    if (open !== 'reverse') return;
    const h = setTimeout(() => { api.jnlPosted(clientId, periodId, q).then((d: any) => setPosted(d.entries || [])).catch(() => setPosted([])); }, q ? 220 : 0);
    return () => clearTimeout(h);
  }, [open, q, clientId, periodId]);

  const dr = lines.reduce((s, l) => s + satang(l.dr), 0);
  const cr = lines.reduce((s, l) => s + satang(l.cr), 0);
  const balanced = dr === cr && dr > 0;
  // The server refuses it anyway (services/manualEntry.ts). Blocking here as well is so the
  // operator is told before they have typed the lines, not after.
  const bankReady = book !== 'bank' || !!bankAcct.trim();

  const reset = () => { setDate(''); setDocNo(''); setNarration(''); setCalcNote(''); setReverseOn(''); setLines(EMPTY); setBook('general');
    if ((bankAccts || []).length !== 1) setBankAcct(''); };

  const save = async () => {
    setBusy('save'); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlManual({
        clientId, periodId, entryDate: date, sourceBook: book, docNo, narration, calcNote,
        bankAccountId: book === 'bank' ? bankAcct.trim() : '',
        autoReverseOn: reverseOn,
        lines: lines.filter(l => l.accountId && (satang(l.dr) || satang(l.cr)))
          .map(l => ({ accountId: l.accountId, dr: satang(l.dr), cr: satang(l.cr) })),
      });
      setMsg(t('pa_manual_saved', { docNo: r.docNo }));
      reset(); setOpen(''); onChanged(); loadTime();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const reverse = async (p: Posted) => {
    if (!window.confirm(t('pa_manual_reverse_confirm', { docNo: p.doc_no }))) return;
    setBusy(p.id); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlReverse(p.id);
      setMsg(t('pa_manual_reversed_msg', { docNo: r.docNo, date: dmy(r.date) }));
      setPosted(null); setQ(q => q); onChanged();
      api.jnlPosted(clientId, periodId, q).then((d: any) => setPosted(d.entries || []));
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const sweep = async () => {
    setBusy('sweep'); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlDueReversals(clientId, periodId);
      setMsg(r.created ? t('pa_manual_sweep_done', { n: r.created }) : t('pa_manual_sweep_none'));
      onChanged();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-pencil-plus" style={{ fontSize: 18, color: 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_manual_title')}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {t('pa_manual_subtitle')}
          </span>
        </span>
        {!periodClosed && (<>
          <button onClick={() => { setOpen(o => o === 'new' ? '' : 'new'); setErr(''); }} style={{ ...btn, background: open === 'new' ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
            <i className="ti ti-plus" aria-hidden /> {t('pa_manual_new_btn')}
          </button>
          <button onClick={() => { setOpen(o => o === 'reverse' ? '' : 'reverse'); setErr(''); }} style={{ ...btn, background: open === 'reverse' ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
            <i className="ti ti-arrow-back-up" aria-hidden /> {t('pa_manual_reverse_btn')}
          </button>
          <button onClick={sweep} disabled={!!busy} title={t('pa_manual_sweep_title')} style={btn}>
            <i className={busy === 'sweep' ? 'ti ti-loader' : 'ti ti-refresh'} aria-hidden /> {t('pa_manual_accruals_btn')}
          </button>
        </>)}
      </div>

      {/* The DBD's entry deadlines, stated where the entries are made rather than in a report nobody opens. */}
      {time && (!!time.lateEntries?.length || time.closingOverdue) && (
        <div style={{ fontSize: 11.5, color: '#92400E', background: '#FFFBEB', borderTop: '1px solid #F1C232', padding: '8px 13px', lineHeight: 1.7 }}>
          {!!time.lateEntries?.length && (
            <div><i className="ti ti-clock-exclamation" aria-hidden /> {t('pa_manual_late_entries', { n: time.lateEntries.length, days: time.lateEntries[0].days, docNo: time.lateEntries[0].docNo })}
              {' '}{t('pa_manual_late_rule')}</div>
          )}
          {time.closingOverdue && (
            <div><i className="ti ti-alert-triangle" aria-hidden /> {t('pa_manual_closing_overdue', { date: dmy(time.closingOverdue.endDate), days: time.closingOverdue.daysSince })}</div>
          )}
        </div>
      )}

      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderTop: '1px solid #BBE5CB', padding: '8px 13px' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {open === 'new' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_manual_f_date')}<br />
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, width: 150 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_manual_f_book')}<br />
              <select value={book} onChange={e => setBook(e.target.value)} style={{ ...inp, width: 170 }}>
                {BOOKS.map(([k, nameKey, glossKey]) => <option key={k} value={k}>{`${t(nameKey)} · ${t(glossKey)}`}</option>)}
              </select></label>
            {book === 'bank' && (
              <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                {t('pa_manual_f_bank_acct')} <span style={{ color: 'var(--text-tertiary)' }}>{t('pa_manual_f_bank_acct_hint')}</span><br />
                {bankTyping || !(bankAccts || []).length ? (
                  <>
                    <input value={bankAcct} onChange={e => setBankAcct(e.target.value)}
                      placeholder={t('pa_manual_f_bank_acct_ph')} style={{ ...inp, width: 190 }} />
                    {!(bankAccts || []).length && (
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', maxWidth: 330, marginTop: 3, lineHeight: 1.45 }}>
                        {t('pa_manual_bank_acct_none')}
                      </div>
                    )}
                  </>
                ) : (
                  <select value={bankAcct}
                    onChange={e => { if (e.target.value === '\u0000new') { setBankTyping(true); setBankAcct(''); } else setBankAcct(e.target.value); }}
                    style={{ ...inp, width: 190 }}>
                    <option value=""></option>
                    {(bankAccts || []).map(a => (
                      <option key={a.id} value={a.id}>{a.entries ? `${a.id} · ${t('pa_manual_bank_acct_used', { n: a.entries })}` : a.id}</option>
                    ))}
                    <option value={'\u0000new'}>{t('pa_manual_bank_acct_new')}</option>
                  </select>
                )}
              </label>
            )}
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_manual_f_docno')} <span style={{ color: 'var(--text-tertiary)' }}>{t('pa_manual_f_docno_hint')}</span><br />
              <input value={docNo} onChange={e => setDocNo(e.target.value)} placeholder={t('pa_manual_f_docno_ph')} style={{ ...inp, width: 190 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_manual_f_reverse_on')} <span style={{ color: 'var(--text-tertiary)' }}>{t('pa_manual_f_reverse_on_hint')}</span><br />
              <input type="date" value={reverseOn} onChange={e => setReverseOn(e.target.value)} style={{ ...inp, width: 150 }} /></label>
          </div>
          <input value={narration} onChange={e => setNarration(e.target.value)} placeholder={t('pa_manual_narration_ph')}
            style={{ ...inp, width: '100%', marginBottom: 7 }} />
          <input value={calcNote} onChange={e => setCalcNote(e.target.value)}
            placeholder={t('pa_manual_calcnote_ph')}
            style={{ ...inp, width: '100%', marginBottom: 9 }} />

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>
              <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--text-tertiary)', padding: '0 0 4px' }}>{t('pa_manual_th_account')}</th>
              <th style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', width: 130 }}>{t('pa_manual_th_dr')}</th>
              <th style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', width: 130 }}>{t('pa_manual_th_cr')}</th>
              <th style={{ width: 30 }} />
            </tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td style={{ padding: '3px 0' }}>
                    <select value={l.accountId} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, accountId: e.target.value } : x))}
                      style={{ ...inp, width: '100%', maxWidth: 420 }}>
                      <option value="">{t('pa_manual_pick_account')}</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th} — {a.name_en}</option>)}
                    </select>
                  </td>
                  {/* One side per line: typing in one clears the other, which is the CHECK constraint
                      the ledger enforces anyway — better met here than as an error after saving. */}
                  <td style={{ padding: '3px 0 3px 6px' }}>
                    <input value={l.dr} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, dr: e.target.value, cr: '' } : x))}
                      inputMode="decimal" style={{ ...inp, width: '100%', textAlign: 'right' }} /></td>
                  <td style={{ padding: '3px 0 3px 6px' }}>
                    <input value={l.cr} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, cr: e.target.value, dr: '' } : x))}
                      inputMode="decimal" style={{ ...inp, width: '100%', textAlign: 'right' }} /></td>
                  <td style={{ textAlign: 'right' }}>
                    {lines.length > 2 && <button onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}
                      style={{ ...btn, padding: '2px 6px', fontSize: 11, color: '#B42318' }}><i className="ti ti-x" aria-hidden /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={{ paddingTop: 7 }}>
                <button onClick={() => setLines(ls => [...ls, { accountId: '', dr: '', cr: '' }])} style={{ ...btn, padding: '4px 9px', fontSize: 11.5 }}>
                  <i className="ti ti-plus" aria-hidden /> {t('pa_manual_add_line')}</button>
              </td>
              <td style={{ textAlign: 'right', paddingTop: 7, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{baht(dr)}</td>
              <td style={{ textAlign: 'right', paddingTop: 7, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{baht(cr)}</td>
              <td />
            </tr></tfoot>
          </table>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: balanced ? '#166534' : '#B45309' }}>
              {dr === cr && dr === 0 ? t('pa_manual_no_amounts') : balanced ? t('pa_manual_balances') : t('pa_manual_out_by', { amount: baht(Math.abs(dr - cr)) })}
            </span>
            <div style={{ flex: 1 }} />
            <button onClick={() => { reset(); setOpen(''); }} style={btn}>{t('cancel')}</button>
            <button onClick={save} disabled={!balanced || !bankReady || !!busy}
              style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: (!balanced || !bankReady || busy) ? 0.5 : 1 }}>
              <i className={busy === 'save' ? 'ti ti-loader' : 'ti ti-device-floppy'} aria-hidden /> {t('pa_manual_save_draft')}
            </button>
          </div>
        </div>
      )}

      {open === 'reverse' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.6 }}>
            {t('pa_manual_reverse_note')}
          </div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('pa_manual_search_ph')}
            style={{ ...inp, width: '100%', marginBottom: 9 }} />
          {posted === null ? <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
            : !posted.length ? <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12.5 }}>{t('pa_manual_none_posted')}</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {posted.map(p => (
                    <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 6px 6px 0', whiteSpace: 'nowrap', color: 'var(--text-tertiary)' }}>{dmy(p.entry_date)}</td>
                      <td style={{ padding: '6px', fontWeight: 700 }}>{p.doc_no}</td>
                      <td style={{ padding: '6px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.narration}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{baht(p.amount)}</td>
                      <td style={{ padding: '6px 0 6px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {p.reversed_by
                          ? <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('pa_manual_reversed_by', { docNo: p.reversed_by })}</span>
                          : <button onClick={() => reverse(p)} disabled={!!busy} style={{ ...btn, padding: '3px 9px', fontSize: 11.5 }}>{t('pa_manual_reverse_btn')}</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}
    </div>
  );
}
